import { Box, Typography, useTheme, Card, CardHeader, CardContent, Paper } from "@mui/material";
import ISODateTime from "components/formatting/ISODateTime";
import TruncatedString from "components/formatting/TruncatedString";
import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import APIRequest from "system/APIRequest";

export interface DashboardTemplateLogEntry {
  created_at?: string;
  dashboard: {
    id: string;
    name: string;
  }
  global_query_id?: {
    id: string;
    name: string;
  };
  queries: Array<{
    id: string;
    name: string;
    color?: string;
  }>;
}

export default function RecentQuickSearches(): JSX.Element {
  const theme = useTheme();
  const navigate = useNavigate();
  const { i18n } = useTranslation();

  const [recentSearches, setRecentSearches] = React.useState<Array<DashboardTemplateLogEntry>>([]);

  useEffect(() => {
    APIRequest(`dashboard-template-log`, {}, { method: "GET" }).then((response: any) => {
      const entries = (response.entries || []).map((entry: any) => {
        const row = {...entry};
        row.queries = (row.queries || []).map((query: any, index: number) => {
          return { ...query, color: query.color || theme.colors.namedColors.chartColorArray[index % theme.colors.namedColors.chartColorArray.length] };
        });
        return row;
      });
      setRecentSearches(entries || []);
    });
  }, []);

  function navigateToRecentSearch(entry: DashboardTemplateLogEntry) {
    const dashboardId = entry.dashboard.id;
    const globalQueryId = entry.global_query_id?.id;
    const queryIds = entry.queries.map((query) => query.id);
    const queryString = queryIds.join("-") + (globalQueryId ? `-g_${globalQueryId}` : "");
    navigate(`/dashboard/${dashboardId}/${queryString}`);
  }

  return (
    <Paper sx={{ overflow: "hidden", p: 0, backgroundColor: theme.palette.background.default, borderTopLeftRadius: theme.general.borderRadius, borderTopRightRadius: theme.general.borderRadius }}>
      <Box sx={{ borderBottom: `1px solid ${theme.palette.divider}`, backgroundColor: theme.palette.background.paper, p: 2, py: 0, borderTopLeftRadius: theme.general.borderRadius, borderTopRightRadius: theme.general.borderRadius }}>
        <Typography variant="h3" sx={{ display: "block", fontSize: "1.3rem", textAlign:"left", pt: 1, my:1, color: theme.palette.text.secondary }}>
          {i18n.t("widget-types.template-search.recent-searches")}
        </Typography>
      </Box>
      <Box sx={{ backgroundColor: theme.palette.background.default, pt: 1 }}>
        {recentSearches.map((entry: DashboardTemplateLogEntry, index: number) => (
          <Paper key={index}
            sx={{
              m: 1, mt: 0, mb: 1, py: "7px", pr: "10px", pl: "15px",
              minHeight: "75px", cursor: "pointer",
              display: "flex", alignItems: "center",
              "&:hover": { backgroundColor: theme.palette.action.hover } }}
            onClick={() => {
              navigateToRecentSearch(entry);
            }}>
            <Box sx={{ display: "grid", gridTemplateColumns: "250px auto", gap: 1, alignItems: "center", width: "100%" }}>
              <Box>
                <Typography variant="h4" sx={{ color: theme.palette.text.secondary }}>
                  {entry.dashboard.name}
                </Typography>
                <ISODateTime typographyVariant="body2" sx={{ color: theme.palette.text.secondary }} isoString={entry.created_at || ""} />
              </Box>
              <Box sx={{ textAlign: "right" }}>
                {entry.queries.map((query) => (
                  <Box
                    key={query.id}
                    sx={{
                      m: 0.4, mx: 0.3, pt: "5px", pb: "6px", pl: "12px", pr: "14px",
                      display: "inline-block",
                      backgroundColor: query.color || theme.colors.namedColors.chart1,
                      borderRadius: "15px", fontWeight: "bold", fontSize: "0.8rem",
                      color: theme.palette.getContrastText(query.color || theme.colors.namedColors.chart1)
                    }}>
                    <TruncatedString text={query.name} maxLength={20} />
                  </Box>
                ))}
              </Box>
            </Box>
          </Paper>
        ))}
      </Box>
    </Paper>
  );
}
